import {MAX_TIME_BONUS, PERSONAS, REDACT_DURATION} from './constants'
import type {Answer, Persona, Question} from './types'

const USERNAME_ADJECTIVES = [
  'Swift',
  'Sharp',
  'Stealth',
  'Steady',
  'Savvy',
  'Silent',
  'Clever',
  'Bold',
  'Chief',
  'Iron',
  'Keen',
  'Elite',
  'Bright',
  'Prime',
  'Agile',
  'Turbo',
  'Hyper',
  'Rapid',
  'Sonic',
  'Ultra',
  'Stellar',
  'Apex',
  'Brisk',
  'Covert',
  'Deft',
  'Fierce',
  'Grand',
  'Nova',
  'Polar',
  'Quantum',
  'Rebel',
  'Solar',
  'Titan',
  'Vivid',
  'Noble',
  'Zen',
  'Nimble',
  'Frosty',
  'Mighty',
  'Lucky',
]

const USERNAME_NOUNS = [
  'Auditor',
  'Inspector',
  'Detective',
  'Guardian',
  'Warden',
  'Sentinel',
  'Ranger',
  'Marshal',
  'Agent',
  'Falcon',
  'Enforcer',
  'Tracker',
  'Analyst',
  'Cipher',
  'Striker',
  'Scout',
  'Phantom',
  'Vanguard',
  'Pioneer',
  'Lynx',
  'Hawk',
  'Panther',
  'Heron',
  'Sparrow',
  'Raven',
  'Dolphin',
  'Otter',
  'Badger',
  'Fox',
  'Wolf',
  'Condor',
  'Osprey',
  'Puma',
  'Mantis',
  'Eagle',
  'Raptor',
  'Maverick',
  'Oracle',
  'Nomad',
  'Sphinx',
]

export const generateUsername = (): string => {
  const adj = USERNAME_ADJECTIVES[Math.floor(Math.random() * USERNAME_ADJECTIVES.length)]
  const noun = USERNAME_NOUNS[Math.floor(Math.random() * USERNAME_NOUNS.length)]
  return `${adj}${noun}`
}

const MIN_TIME_MS: Record<string, number> = {
  'true-false': 2000,
  fermi: 3000,
  'multiple-choice': 3000,
  'phishing-email': 5000,
  'invoice-master-data': 5000,
}

const GRACE_PERIOD_MS: Record<string, number> = {
  'true-false': 10000,
  fermi: 15000,
  'multiple-choice': 20000,
  'phishing-email': 15000,
  'invoice-master-data': 15000,
}

export const computeFinalScore = (
  answers: Answer[],
  questions: Question[],
): {preciseScore: number; displayScore: number; captchaRequired: boolean} => {
  const totalPoolPoints = questions.reduce((sum, q) => sum + q.points, 0)
  let accuracyScore = 0
  let timeBonus = 0
  let suspicious = false

  for (const answer of answers) {
    const question = questions.find((q) => q.id === answer.questionId)

    if (!question) {
      continue
    }

    accuracyScore += answer.score

    const timeAllowedMs = question.timerSeconds * 1000
    const gracePeriodMs = GRACE_PERIOD_MS[question.type] ?? 15000
    const bonusWindowMs = timeAllowedMs - gracePeriodMs
    const timeAfterGrace = Math.max(0, answer.elapsedMs - gracePeriodMs)
    const weight = totalPoolPoints > 0 ? MAX_TIME_BONUS * (question.points / totalPoolPoints) : 0

    const bonusRatio = bonusWindowMs > 0 ? Math.max(0, 1 - timeAfterGrace / bonusWindowMs) : 0
    const questionTimeBonus = answer.correct && bonusRatio > 0 ? weight * Math.sqrt(bonusRatio) : 0

    timeBonus += questionTimeBonus

    if (answer.elapsedMs < (MIN_TIME_MS[question.type] ?? 3000)) {
      suspicious = true
    }
  }

  const preciseScore = accuracyScore + Math.min(timeBonus, MAX_TIME_BONUS)

  return {preciseScore, displayScore: Math.round(preciseScore), captchaRequired: suspicious}
}

export const getPersona = (score: number): Persona =>
  PERSONAS.find((p) => score >= p.minScore) ?? PERSONAS[PERSONAS.length - 1]

export const shuffle = <T>(arr: T[]): T[] => {
  const copy = [...arr]

  for (let i = copy.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1))

    ;[copy[i], copy[j]] = [copy[j], copy[i]]
  }

  return copy
}

const pickPerGroup = (questions: Question[], count: number): Question[] => {
  const grouped = new Map<string, Question[]>()
  const ungrouped: Question[] = []

  for (const q of questions) {
    if (q.questionGroup) {
      const group = grouped.get(q.questionGroup) ?? []
      group.push(q)
      grouped.set(q.questionGroup, group)
    } else {
      ungrouped.push(q)
    }
  }

  const picked: Question[] = []

  for (const [, group] of grouped) {
    const shuffled = shuffle(group)
    picked.push(...shuffled.slice(0, count))
  }

  return shuffle([...picked, ...ungrouped])
}

export const shuffleAndDraw = (pool: Question[]): Question[] => {
  const phase1 = pickPerGroup(
    pool.filter((q) => q.phase === 1),
    1,
  )

  const phase2Pool = pool.filter((q) => q.phase === 2)
  const emailQuestions = shuffle(phase2Pool.filter((q) => q.type === 'phishing-email')).slice(0, 2)
  const invoiceQuestions = shuffle(phase2Pool.filter((q) => q.type === 'invoice-master-data')).slice(0, 2)

  return [...phase1, ...emailQuestions, ...invoiceQuestions]
}

export const drawRedaction = (canvas: HTMLCanvasElement) => {
  const ctx = canvas.getContext('2d')

  if (!ctx) {
    return
  }

  const w = canvas.width
  const h = canvas.height
  const strokes = 4
  let frame = 0
  const totalFrames = Math.ceil(REDACT_DURATION / 16)

  const animate = () => {
    frame++
    const progress = Math.min(frame / totalFrames, 1)
    const progressWidth = progress * w
    ctx!.clearRect(0, 0, w, h)

    ctx!.fillStyle = 'rgba(0, 0, 0, 0.95)'
    ctx!.fillRect(0, 2, progressWidth, h - 4)

    for (let s = 0; s < strokes; s++) {
      ctx!.beginPath()
      ctx!.strokeStyle = `rgba(30, 30, 30, ${0.7 + s * 0.1})`
      ctx!.lineWidth = 8 + s * 3
      ctx!.lineCap = 'round'

      const yBase = h / 2 + (s - strokes / 2) * 1.5

      ctx!.moveTo(0, yBase + Math.sin(s) * 1.5)
      const steps = Math.ceil(progressWidth / 4)

      for (let i = 1; i <= steps; i++) {
        const x = (i / steps) * progressWidth
        const wobble = Math.sin(i * 0.8 + s * 1.5) * 1.2
        ctx!.lineTo(x, yBase + wobble)
      }

      ctx!.stroke()
    }

    if (progress > 0.1) {
      const grainCount = Math.floor(progress * 30)

      for (let g = 0; g < grainCount; g++) {
        const gx = Math.random() * progressWidth
        const gy = Math.random() * h
        ctx!.fillStyle = `rgba(0, 0, 0, ${0.3 + Math.random() * 0.4})`
        ctx!.fillRect(gx, gy, 1, 1)
      }
    }

    if (progress < 1) {
      requestAnimationFrame(animate)
    }
  }

  requestAnimationFrame(animate)
}
